import { useState, useRef, useEffect } from 'react'
import { useChannel } from '@/context/ChannelContext'
import { useShowcaseAccess } from '@/showcase/access'
import { SHOWCASE_ENABLED } from '@/config/showcase'
import type { Channel } from '@/api/channels'

function getInitials(title: string): string {
  return title
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
}

export function ProjectSelector() {
  const { channels, selectedChannel, selectChannel, channelFilter, setChannelFilter } = useChannel()
  // Search + группировка «Мои / Другие» + toggle фильтра — только для superadmin.
  // Обычному юзеру у которого 1-2 своих канала это не нужно и загромождает UI.
  const isSuperadmin = useShowcaseAccess()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const ref = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  // При закрытии — очищаем query.
  useEffect(() => {
    if (!open) setQuery('')
  }, [open])

  const q = query.trim().toLowerCase()
  const filtered = q
    ? channels.filter(
        (c) =>
          c.title.toLowerCase().includes(q) ||
          c.maxChannelID.includes(q) ||
          c.uuid.toLowerCase().includes(q),
      )
    : channels
  // Свои каналы наверх, чужие внизу — visual separation для superadmin.
  const ownChannels = filtered.filter((c) => !c.isForeign)
  const foreignChannels = filtered.filter((c) => c.isForeign)

  if (!selectedChannel) {
    return (
      <div className="ps-empty">
        Нет каналов
      </div>
    )
  }

  return (
    <div className="ps-wrap" ref={ref}>
      <button className="ps-trigger" onClick={() => setOpen((v) => !v)}>
        <div className="ps-avatar">
          {selectedChannel.iconUrl ? (
            <img src={selectedChannel.iconUrl} alt="" />
          ) : (
            <span>{getInitials(selectedChannel.title)}</span>
          )}
        </div>
        <div className="ps-meta">
          <div className="ps-name" title={selectedChannel.title}>
            {selectedChannel.title}
          </div>
          <div className="ps-sub">
            {channels.length === 1 ? '1 проект' : `${channels.length} ${channels.length < 5 ? 'проекта' : 'проектов'}`}
          </div>
        </div>
        <svg className="ps-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M8 9l4-4 4 4M8 15l4 4 4-4" />
        </svg>
      </button>

      {open && (
        <div className="ps-popup" role="listbox">
          {isSuperadmin && (
            <>
              {SHOWCASE_ENABLED && (
                <div className="ps-filter">
                  <button
                    className={`ps-filter-btn ${channelFilter === 'showcase' ? 'active' : ''}`}
                    onClick={() => setChannelFilter('showcase')}
                  >
                    Только Витрина
                  </button>
                  <button
                    className={`ps-filter-btn ${channelFilter === 'all' ? 'active' : ''}`}
                    onClick={() => setChannelFilter('all')}
                  >
                    Все каналы
                  </button>
                </div>
              )}
              <div className="ps-search">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="7" />
                  <path d="m21 21-4.3-4.3" />
                </svg>
                <input
                  ref={inputRef}
                  autoFocus
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Найти канал по названию или ID"
                />
                {query && (
                  <button className="ps-search-clear" onClick={() => setQuery('')} aria-label="Очистить">
                    ×
                  </button>
                )}
              </div>
            </>
          )}

          {isSuperadmin ? (
            filtered.length === 0 ? (
              <div className="ps-noresults">Ничего не нашлось</div>
            ) : (
              <>
                {ownChannels.length > 0 && (
                  <>
                    {foreignChannels.length > 0 && <div className="ps-group">Мои каналы</div>}
                    {ownChannels.map((c) => (
                      <ChannelOption
                        key={c.uuid}
                        channel={c}
                        active={c.uuid === selectedChannel.uuid}
                        onSelect={() => {
                          selectChannel(c.uuid)
                          setOpen(false)
                        }}
                      />
                    ))}
                  </>
                )}
                {foreignChannels.length > 0 && (
                  <>
                    <div className="ps-group">Другие каналы (админ-режим)</div>
                    {foreignChannels.map((c) => (
                      <ChannelOption
                        key={c.uuid}
                        channel={c}
                        active={c.uuid === selectedChannel.uuid}
                        onSelect={() => {
                          selectChannel(c.uuid)
                          setOpen(false)
                        }}
                      />
                    ))}
                  </>
                )}
              </>
            )
          ) : (
            // Обычные юзеры видят простой список — как было до superadmin-режима.
            channels.map((c) => (
              <ChannelOption
                key={c.uuid}
                channel={c}
                active={c.uuid === selectedChannel.uuid}
                onSelect={() => {
                  selectChannel(c.uuid)
                  setOpen(false)
                }}
              />
            ))
          )}
        </div>
      )}

      <style>{`
        .ps-wrap {
          position: relative;
        }
        .ps-empty {
          padding: 12px;
          background: var(--tg-surface-2, #f5f5f9);
          border-radius: 12px;
          font-size: 13px;
          color: var(--tg-mute, #888);
          text-align: center;
        }
        .ps-trigger {
          width: 100%;
          padding: 12px;
          background: var(--tg-surface-2, #f5f5f9);
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          border: none;
          transition: background 150ms;
          text-align: left;
        }
        .ps-trigger:hover {
          background: var(--tg-blue-bg, #ebf2ff);
        }
        .ps-avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: linear-gradient(135deg, #FF8C42, #FF4000);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 700;
          font-size: 11px;
          text-transform: uppercase;
          flex-shrink: 0;
          overflow: hidden;
        }
        .ps-avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .ps-avatar.small {
          width: 28px;
          height: 28px;
          font-size: 10px;
        }
        .ps-meta {
          flex: 1;
          min-width: 0;
        }
        .ps-name {
          font-size: 13px;
          font-weight: 600;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          color: var(--tg-ink, #111);
        }
        .ps-sub {
          font-size: 11px;
          color: var(--tg-mute, #888);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .ps-chev {
          width: 14px;
          height: 14px;
          color: var(--tg-mute, #888);
          flex-shrink: 0;
        }
        .ps-popup {
          position: absolute;
          bottom: calc(100% + 8px);
          left: 0;
          right: 0;
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 12px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.08);
          padding: 4px;
          max-height: 320px;
          overflow-y: auto;
          z-index: 100;
        }
        .ps-option {
          width: 100%;
          padding: 10px;
          background: transparent;
          border: none;
          border-radius: 8px;
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          text-align: left;
          transition: background 100ms;
        }
        .ps-option:hover {
          background: var(--tg-surface-2, #f5f5f9);
        }
        .ps-option.active {
          background: var(--tg-blue-bg, #ebf2ff);
        }
        .ps-filter {
          display: flex;
          gap: 4px;
          padding: 8px 10px 4px;
        }
        .ps-filter-btn {
          flex: 1;
          padding: 6px 8px;
          background: transparent;
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 6px;
          font-family: inherit;
          font-size: 11.5px;
          font-weight: 600;
          color: var(--tg-mute, #888);
          cursor: pointer;
          transition: all 100ms;
        }
        .ps-filter-btn:hover { background: var(--tg-surface-2, #f5f5f9); }
        .ps-filter-btn.active {
          background: var(--tg-blue, #0080ff);
          color: white;
          border-color: var(--tg-blue, #0080ff);
        }
        .ps-search {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 10px;
          border-bottom: 1px solid var(--tg-line-2, #f0f0f4);
          margin-bottom: 4px;
        }
        .ps-search svg { width: 14px; height: 14px; color: var(--tg-mute, #888); flex-shrink: 0; }
        .ps-search input {
          flex: 1;
          border: 0;
          background: transparent;
          outline: none;
          font-family: inherit;
          font-size: 13px;
          color: var(--tg-ink, #111);
        }
        .ps-search input::placeholder { color: var(--tg-mute, #999); }
        .ps-search-clear {
          background: transparent;
          border: 0;
          color: var(--tg-mute, #888);
          font-size: 18px;
          line-height: 1;
          padding: 0 4px;
          cursor: pointer;
        }
        .ps-group {
          padding: 8px 10px 4px;
          font-size: 10.5px;
          font-weight: 800;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--tg-mute, #999);
        }
        .ps-noresults {
          padding: 20px 10px;
          text-align: center;
          font-size: 12.5px;
          color: var(--tg-mute, #888);
        }
        .ps-foreign-badge {
          padding: 1px 6px;
          font-size: 9.5px;
          font-weight: 700;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: var(--tg-mute, #666);
          background: var(--tg-surface-2, #f0f0f4);
          border-radius: 4px;
          margin-left: 6px;
        }
      `}</style>
    </div>
  )
}

function ChannelOption({
  channel,
  active,
  onSelect,
}: {
  channel: Channel
  active: boolean
  onSelect: () => void
}) {
  return (
    <button
      className={`ps-option ${active ? 'active' : ''}`}
      onClick={onSelect}
      role="option"
      aria-selected={active}
    >
      <div className="ps-avatar small">
        {channel.iconUrl ? <img src={channel.iconUrl} alt="" /> : <span>{getInitials(channel.title)}</span>}
      </div>
      <div className="ps-meta">
        <div className="ps-name" title={channel.title}>
          {channel.title}
          {channel.isForeign && <span className="ps-foreign-badge">чужой</span>}
        </div>
        <div className="ps-sub">
          {channel.tariff === 'profi' ? 'Профи' : channel.tariff === 'free' ? 'Бесплатный' : channel.tariff}
          {' · '}
          {channel.memberCount.toLocaleString('ru-RU')} подписчиков
        </div>
      </div>
    </button>
  )
}
